import { scaleLinear, scaleSqrt } from 'd3-scale';
import { line, curveMonotoneX } from 'd3-shape';

import dayjs from 'dayjs';

export const getDateSuffix = (language, day) => {
  if (language !== 'en') {
    return '';
  }

  if (day === 1) {
    return 'st';
  }

  if (day === 2) {
    return 'nd';
  }

  if (day === 3) {
    return 'rd';
  }

  return 'th';
};

export const ticksMaxDomain = 100;
export const viewboxHeight = 50;
export const viewboxWidth = 100;
export const xAxisTicksRange = [0, ticksMaxDomain];
export const yAxisTicksRange = [ticksMaxDomain, 0];
export const yAxisTicksRangeInverted = [0, ticksMaxDomain];
export const ticksLength = 1;

export const getLinearScale = (range, domain) => scaleLinear()
  .range(range)
  .domain(domain);

export const getSqrtScale = (range, domain) => scaleSqrt()
  .range(range)
  .domain(domain);

/**
 * Create an object as { [id value]: attribute value }
 * from an array [{ [id]: id value, [attribute]: attribute value }, ...]
 *
 * @param {array of object} array
 * @param {string} id
 * @param {string} attribute
 * @returns {object}
 */
const createObjectFromArray = (array, id, attribute) => array.reduce((result, item) => {
  // eslint-disable-next-line no-param-reassign
  result[item[id]] = item[attribute];
  return result;
}, {});

/* Bubble chart */
export const radiusRange = [0, 4.5];
export const padding = 2;
export const temperatureMarginBottomPx = 3;
// start at 3 days because no temperature wave cannot happen under 3 days
export const startDelay = 2; //3
export const rightDaysDelayMargin = 3;

export const getStartDelay = (period, type) => {
    if (period !== 'passe' && type === 'chaleur') {
      result = 0;
    }
    else {
      result = 3;
    }
 
    return result;
}



/**
 * compute margin top (viewbox px) from the radius of the item with the largest surface
 * within top 3 uppest temperature
 * and define the corresponding pixel domain
 *
 * @param {object}
    * @param {array} allSeries data for the chart
    * @param {string} type type of the chart : 'chaleur' / 'froid'
    * @param {string} accessor name of the data variable to access the data value for temperature
    * @param {functon} radiusScale data for the chart
    * @param {string} axis 'y' / 'x'
 * @returns {array}
 */
export const getTemperatureDomain = ({ allSeries = [], type, accessor = 'y', radiusScale, axis = 'y' }) => {
  allSeries.sort((a, b) => {
    let result = b - a;
    if (axis === 'y') {
      result = type === 'chaleur' ? b[accessor] - a[accessor] : a[accessor] - b[accessor];
    }
    if (axis === 'x') {
      result = b[accessor] - a[accessor];
    }
    return result;
  });
  const top3 = allSeries.slice(0, 3).sort((a, b) => b.s - a.s);
  const upperItemRadius = top3 && top3.length > 1 && top3[0].s;
  const marginTop = radiusScale(upperItemRadius) + padding;
  const marginBottom = axis === 'y' ? temperatureMarginBottomPx : 0;

  let domain = [0, 0];
  if (axis === 'y') {
    const initialDomain = type === 'chaleur' ? [viewboxHeight, 0] : [0, viewboxHeight];
    domain = type === 'chaleur'
      ? [initialDomain[0] - marginBottom, initialDomain[1] + marginTop]
      : [initialDomain[0] + marginTop, initialDomain[1] - marginBottom];
  }
  if (axis === 'x') {
    domain = [0, viewboxWidth - marginTop];
  }

  return domain;
};

/**
 * Get the domain for the chart labels
 *
 * @param {int} currentViewboxSize
 * @param {int} currentTicksMaxDomain
 * @param {array} sourceDomain
 * @returns {array}
 */
export const getDomainLabel = (currentViewboxSize, currentTicksMaxDomain, sourceDomain) => {
  if (!sourceDomain || sourceDomain.length !== 2) {
    return null;
  }

  const ratio = currentTicksMaxDomain / currentViewboxSize;
  return [sourceDomain[0] * ratio, sourceDomain[1] * ratio];
};

/**
 * Parse date from a data series
 * series array expect object with two dates { periode : { d1, d2 }}
 * and overide them with a parsed date with dayjs
 *
 * @param {array} serie
 * @returns {array}
 */
export const parseDate = (serie = []) => serie.map(item => ({
  d1: dayjs(item?.periode?.d1, 'YYYY-M-D'),
  d2: dayjs(item?.periode?.d2, 'YYYY-M-D'),
  ...item,
}));

export const greyGradientId = 'grey';

export const bubbleChartColors = {
  yellow: { start: '#FFF6E5', mid: '#EFC777', last: '#B28223' },
  blue: { start: '#E5F6FF', mid: '#79C9F2', last: '#2382B2' },
  darkBlue: { start: '#dce4fe', mid: '#8da7f4', last: '#264bb6' },
  darkPink: { start: '#fdd6df', mid: '#ef7795', last: '#b22247' },
  violet: { start: '#e7d2fd', mid: '#ae71ec', last: '#722ab8' },
  darkOrange: { start: '#fce0d1', mid: '#eb9b72', last: '#b45526' },
  grey: { start: '#dfe2e6', mid: '#cccccc', last: '#a5a5a5' },
};
export const vagueTemperatureAttributes = [
  { code: 'passe', label: 'event_passe_short', visibility: true, disabled: true, color: null },
  { code: 'chaleur', label: 'event_passe_short', visibility: true, disabled: true, color: bubbleChartColors.yellow },
  { code: 'froid', label: 'event_passe_short', visibility: true, disabled: true, color: bubbleChartColors.blue },
  { code: 'rcp26Horizon1', label: 'event_hm_26_short', visibility: false, disabled: false, color: bubbleChartColors.darkBlue },
  { code: 'rcp26Horizon2', label: 'event_hl_26_short', visibility: false, disabled: false, color: bubbleChartColors.darkPink },
  { code: 'rcp85Horizon1', label: 'event_hm_85_short', visibility: false, disabled: false, color: bubbleChartColors.violet },
  { code: 'rcp85Horizon2', label: 'event_hl_85_short', visibility: false, disabled: false, color: bubbleChartColors.darkOrange },
  { code: greyGradientId, label: null, visibility: false, disabled: false, color: bubbleChartColors.grey },
];

export const vagueTemperatureColor = createObjectFromArray(vagueTemperatureAttributes, 'code', 'color');
export const vagueTemperaturelabels = createObjectFromArray(vagueTemperatureAttributes, 'code', 'label');
export const vagueTemperatureVisibility = createObjectFromArray(vagueTemperatureAttributes, 'code', 'visibility');
export const vagueTemperatureDisabled = createObjectFromArray(vagueTemperatureAttributes, 'code', 'disabled');

export const getBubbleChartType = param => {
  if (param === 'FROID') { return 'froid'; }
  if (param === 'CHALE') { return 'chaleur'; }
  return null;
};

/**
 * Create an array of multiple from 0 to the provided upperBound
 * ex : 5 multiples up to 20 will provide [5, 10, 15, 20]
 *
 * @param {int} multiple
 * @param {int} upperBound
 * @returns {array}
 */
export const getMultiples = (multiple, upperBound) =>
  [...(Array(Math.floor(upperBound / multiple)))].map((_, i) => multiple * (i + 1));

export const bubbleChartXaxisLabelMultiple = 5;

/**
 * Check if both date's year are including in the year array
 * @param {dayjs date} d1
 * @param {dayjs date} d2
 * @param {array} range
 * @returns {boolean}
 */
export const isBetween = (d1, d2, range = null) => {
  if (!range) {
    return true;
  }
  return d1.year() >= range[0] && d2.year() <= range[1];
};

/**
 * Parse bubble chart data as per the JSON format provided
 * which is a bit different between PASSE and FUTUR
 * @param {object} data
 * @param {string} period
 * @param {string} type
 * @returns {object}
 *  @returns {array} allSeries : merge of the series data array
 *  @returns {array} series : description of the available data series
 *  @returns {array} yearsBounds : [yearMin, yearMax] of the data
 *  of the checkboxes that control the series visibility
 */
export const parseBubbleChartData = (data = { tab: [] }, period = 'PASSE', type = 'chaleur') => {
  let allSeries;
  let series;
  let yearsBounds = null;

  if (period === 'PASSE') {
    allSeries = parseDate(data?.tab);
    series = [{ code: 'passe', data: allSeries }];

    if (data?.periode?.mn && data?.periode?.mx) {
      yearsBounds = [data?.periode?.mn, data?.periode?.mx];
    }
  } else {
    const {
      futur_rcp26_1: rcp26Horizon1 = [],
      futur_rcp26_2: rcp26Horizon2 = [],
      futur_rcp85_1: rcp85Horizon1 = [],
      futur_rcp85_2: rcp85Horizon2 = [],
      passe,
    } = data;
    allSeries = [
      ...rcp26Horizon1, ...rcp26Horizon2, ...rcp85Horizon1, ...rcp85Horizon2,
      ...passe,
    ];

    series = [
      { code: type, data: parseDate(passe) },
      { code: 'rcp26Horizon1', data: parseDate(rcp26Horizon1) },
      { code: 'rcp26Horizon2', data: parseDate(rcp26Horizon2) },
      { code: 'rcp85Horizon1', data: parseDate(rcp85Horizon1) },
      { code: 'rcp85Horizon2', data: parseDate(rcp85Horizon2) },
    ];
  }

  return { allSeries, series, yearsBounds, period };
};

/* Line chart */

/**
 * Create the line path from x, y accessor and x, y scales
 * @param {function} getX x function accessor
 * @param {function} getY y function accessor
 * @param {function} xScale
 * @param {function} yScale
 * @returns {function}
 */
export const getLinePath = (getX, getY, xScale, yScale) => line()
  .x(d => xScale(getX(d)))
  .y(d => yScale(getY(d)))
  .curve(curveMonotoneX);

export const SWIAttributes = [
  { code: 'min', short: 'record_secs', long: 'record_secs_long', color: '#ab9d7a' },
  { code: 'max', short: 'record_humides', long: 'record_humides_long', color: '#62b8d0' },
  { code: 'moy1', short: 'moy_61_90', long: 'moy_61_90', color: '#f09f2a' },
  { code: 'moy2', short: 'moy_91_20', long: 'moy_91_20', color: '#68ba99' },
  { code: 'pes2', short: 'pes2', long: 'pes2_long', color: '#95de1f' },
  { code: 'pes1', short: 'pes1', long: 'pes1_long', color: '#bd83b0' },
];
// moyenne 1981-2010 :  { code: 'moy2', short: 'moy_81_10', long: 'moy_81_10', color: '#68ba99' },

export const SWIcheckboxLabels = createObjectFromArray(SWIAttributes, 'code', 'short');
export const SWItooltipLabels = createObjectFromArray(SWIAttributes, 'code', 'long');
export const SWIcolors = createObjectFromArray(SWIAttributes, 'code', 'color');

const dateEndRange = [
  [21, 31],
  [21, 28],
  [21, 31],
  [21, 30],
  [21, 31],
  [21, 30],
  [21, 31],
  [21, 31],
  [21, 30],
  [21, 31],
  [21, 30],
  [21, 31],
];

export const dateRanges = month => [
  [1, 10],
  [11, 20],
  dateEndRange[month],
];

export const SWITextAnnotations = [
  { label: 'label_sec', position: 0.35 },
  { label: 'label_humide', position: 0.45 },
  { label: 'label_tres_humide', position: 0.8 },
];

export const annotationsTextMargin = -1;
export const SWIxMargin = 1;
export const SWIyMargin = 0.15;

// the object key as int represent the step in the tutorial
// in which we should update the series visibility
export const helperSeriesVisibility = {
  PASSE: {
    1: { min: false, max: false, moy1: true, moy2: false },
    3: { min: false, max: false, moy1: true, moy2: true },
    4: { min: true, max: true, moy1: true, moy2: true },
  },
  FUTUR: {
    1: { min: true, max: true, moy1: true, pes1: false, pes2: false },
    3: { min: true, max: true, moy1: true, pes1: true, pes2: false },
    4: { min: true, max: true, moy1: true, pes1: true, pes2: true },
  },
};

/**
 * Compute the [start, end] x values for which their y
 * values are below a defined limit
 * @param {array} serie
 * @param {int} limit
 * @returns {array}
 */
const getRangeBelowLimit = (serie, limit) => {
  if (!limit || !serie) {
    return null;
  }
  const filtered = serie.filter(({ y }) => y <= limit);
  const start = filtered && filtered.length > 0 && filtered[0]?.x;
  const end = filtered && filtered.length > 0 && filtered[filtered.length - 1]?.x;
  return [start, end];
};

export const getHumidPosition = () => SWITextAnnotations.find(({ label }) => label === 'label_humide')?.position;

/**
 * Define limit array to display for each period (PASSE / FUTUR)
 * and help step (2, 3, ...)
 * depending on input data
 * @param {object}
 *
 * @returns {object}
 */
export const getHelpRanges = ({ moy1 = [], moy2 = [], pes1 = [], pes2 = [] }) => {
  const limit = getHumidPosition();
  return {
    PASSE: {
      2: getRangeBelowLimit(moy1, limit),
      3: getRangeBelowLimit(moy2, limit),
    },
    FUTUR: {
      2: getRangeBelowLimit(moy1, limit),
      3: getRangeBelowLimit(pes1, limit),
      4: getRangeBelowLimit(pes2, limit),
    },
  };
};

/**
 * Parse bubble chart data as per the JSON format provided
 * which is a bit different between PASSE and FUTUR
 *
 * @param {*} data
 * @param {*} period
 *  * @returns {object}
 *  @returns {array} allSeries : merge of the series data array
 *  @returns {array} series : description of the available data series
 *  @returns {object} helpRanges : [yearMin, yearMax] of the data
 */
export const parseLineChartData = (data, period) => {
  let allSeries;
  let series;
  let helpRanges;

  if (period === 'PASSE') {
    const {
      curve_min: curveMin = [],
      curve_max: curveMax = [],
      curve_moy1: curveMoy1 = [],
      curve_moy2: curveMoy2 = [],
    } = data;
    allSeries = [...curveMin, ...curveMax, ...curveMoy1, ...curveMoy2];
    helpRanges = getHelpRanges({ moy1: curveMoy1, moy2: curveMoy2 });

    series = [
      { code: 'min', data: curveMin },
      { code: 'max', data: curveMax },
      { code: 'moy1', data: curveMoy1 },
      { code: 'moy2', data: curveMoy2 },
    ];
  } else {
    const {
      curve_futur_min: curveMin = [],
      curve_futur_max: curveMax = [],
      curve_futur_moy1: curveMoy1 = [],
      curve_futur_pes2: curvePes2 = [],
      curve_futur_pes1: curvePes1 = [],
    } = data;
    allSeries = [...curveMin, ...curveMax, ...curveMoy1, ...curvePes1, ...curvePes2];
    helpRanges = getHelpRanges({ moy1: curveMoy1, pes1: curvePes1, pes2: curvePes2 });

    series = [
      { code: 'min', data: curveMin },
      { code: 'max', data: curveMax },
      { code: 'moy1', data: curveMoy1 },
      { code: 'pes1', data: curvePes1 },
      { code: 'pes2', data: curvePes2 },
    ];
  }

  return { allSeries, series, helpRanges };
};
